import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useLocale, useLocalePath, useT } from "../../i18n";
import { api } from "../../lib/api";
import { CountrySelect } from "../forms/CountrySelect";
import { coverImage, formatPrice, vehicleTitle } from "../../lib/format";
import { ResponsiveImage } from "../ResponsiveImage";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const LIMITS = { name: 100, email: 200, phone: 40, destination_port: 100, message: 3000 };
const INPUT = "w-full rounded-none border border-ink/20 bg-white px-4 text-base text-ink transition-colors duration-300 placeholder:text-ink/35 hover:border-ink/45 focus:border-ink focus:outline-none aria-[invalid=true]:border-[#9f1d24]";

const Field = ({ id, label, optional, error, children }) => {
  const t = useT();
  return (
    <div>
      <label htmlFor={id} className="micro-label mb-2 flex items-center gap-2 text-ink/70">
        {label}
        {optional && <span className="normal-case tracking-normal text-ink/40">({t("inquiry.optional")})</span>}
      </label>
      {children}
      {error && <p id={`${id}-error`} className="mt-1.5 text-[13px] text-[#9f1d24]" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
};

const validate = (f, t) => {
  const e = {};
  if (!f.name.trim()) e.name = t("inquiry.errors.required");
  if (!f.country) e.country = t("inquiry.errors.required");
  if (!f.email.trim()) e.email = t("inquiry.errors.required");
  else if (!EMAIL_RE.test(f.email.trim())) e.email = t("inquiry.errors.email");
  Object.entries(LIMITS).forEach(([k, max]) => {
    if (f[k].length > max) e[k] = t("inquiry.errors.tooLong");
  });
  if (!f.consent) e.consent = t("inquiry.errors.consent");
  return e;
};

export const InquiryDialog = ({ open, onOpenChange, vehicle: v }) => {
  const t = useT();
  const lang = useLocale();
  const lp = useLocalePath();
  const blank = { name: "", country: lang === "ja" ? "JP" : "", email: "", phone: "", destination_port: "", message: "", consent: false, website: "" };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("idle");
  const [serverError, setServerError] = useState("");
  const openedAt = useRef(0);
  const img = coverImage(v);

  const handleOpenChange = (next) => {
    if (next) openedAt.current = Date.now();
    if (!next) openedAt.current = 0;
    if (!next && phase === "success") {
      setForm(blank);
      setPhase("idle");
    }
    onOpenChange(next);
  };
  if (open && !openedAt.current) openedAt.current = Date.now();

  const set = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value, ...(k === "country" && value === "JP" ? { destination_port: "" } : {}) }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form, t);
    setErrors(errs);
    setServerError("");
    if (Object.keys(errs).length) return;
    setPhase("sending");
    try {
      await api.post("/inquiries", {
        vehicle_slug: v.slug,
        locale: lang,
        name: form.name.trim(),
        country: form.country,
        email: form.email.trim(),
        phone: form.phone.trim(),
        destination_port: form.country !== "JP" && form.destination_port.trim() ? form.destination_port.trim() : null,
        message: form.message.trim(),
        consent: form.consent,
        website: form.website,
        fill_ms: Date.now() - openedAt.current,
      });
      setPhase("success");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const key =
        status === 429 ? "rate" : status === 404 ? "unavailable" : status === 422 ? "invalid" : detail === "too_fast" ? "tooFast" : "generic";
      setServerError(t(`inquiry.errors.${key}`));
      setPhase("idle");
    }
  };

  const aria = (k) => ({ "aria-invalid": errors[k] ? "true" : undefined, "aria-describedby": errors[k] ? `inq-${k}-error` : undefined });

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          data-testid="inquiry-dialog"
          aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 z-[71] max-h-[92svh] overflow-y-auto overscroll-contain bg-white text-ink outline-none duration-300 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-8 sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[88vh] sm:w-[min(600px,calc(100vw-4rem))] sm:-translate-x-1/2 sm:-translate-y-1/2"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-ink/10 bg-white px-5 py-3 sm:px-8">
            <Dialog.Title className="font-display text-2xl">{t("inquiry.title")}</Dialog.Title>
            <Dialog.Close aria-label={t("inquiry.close")} className="-mr-2 flex h-11 w-11 items-center justify-center" data-testid="inquiry-close">
              <X size={18} strokeWidth={1.25} />
            </Dialog.Close>
          </div>
          <div className="px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5 sm:px-8 sm:pb-8">
            <div className="flex items-center gap-4 border-b border-ink/10 pb-5" data-testid="inquiry-vehicle">
              <div className="relative h-14 w-20 shrink-0 overflow-hidden bg-mist">
                {img && <ResponsiveImage image={img} maxVariant="thumb" sizes="80px" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0">
                <p className="micro-label text-ink/45">{t("inquiry.vehicle")} · {v.stock_id}</p>
                <p className="mt-1 truncate text-[15px] font-medium">{v.model_year} {vehicleTitle(v)}</p>
                <p className="text-sm tabular-nums text-ink/55">{formatPrice(v, t)}</p>
              </div>
            </div>
            {phase === "success" ? (
              <div className="py-12 text-center" data-testid="inquiry-success">
                <p className="font-display text-3xl">{t("inquiry.successTitle")}</p>
                <p className="mx-auto mt-3 max-w-sm text-sm text-ink/60">{t("inquiry.successBody")}</p>
                <Dialog.Close className="btn btn-dark mt-10" data-testid="inquiry-success-close">{t("inquiry.close")}</Dialog.Close>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="mt-6 grid gap-5" data-testid="inquiry-form">
                <Field id="inq-name" label={t("inquiry.name")} error={errors.name}>
                  <input id="inq-name" autoComplete="name" value={form.name} onChange={set("name")} className={`${INPUT} h-12`} data-testid="inquiry-name" {...aria("name")} />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="inq-country" label={t("inquiry.country")} error={errors.country}>
                    <CountrySelect id="inq-country" value={form.country} onChange={(code) => set("country")({ target: { value: code, type: "text" } })} testId="inquiry-country" invalid={!!errors.country} />
                  </Field>
                  {form.country && form.country !== "JP" && (
                    <Field id="inq-destination_port" label={t("inquiry.port")} optional error={errors.destination_port}>
                      <input id="inq-destination_port" value={form.destination_port} onChange={set("destination_port")} className={`${INPUT} h-12`} data-testid="inquiry-port" {...aria("destination_port")} />
                    </Field>
                  )}
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="inq-email" label={t("inquiry.email")} error={errors.email}>
                    <input id="inq-email" type="email" autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} className={`${INPUT} h-12`} data-testid="inquiry-email" {...aria("email")} />
                  </Field>
                  <Field id="inq-phone" label={t("inquiry.phone")} optional error={errors.phone}>
                    <input id="inq-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className={`${INPUT} h-12`} data-testid="inquiry-phone" {...aria("phone")} />
                  </Field>
                </div>
                <Field id="inq-message" label={t("inquiry.message")} optional error={errors.message}>
                  <textarea id="inq-message" rows={4} value={form.message} onChange={set("message")} placeholder={t("inquiry.messagePlaceholder")} className={`${INPUT} resize-y py-3`} data-testid="inquiry-message" {...aria("message")} />
                </Field>
                <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="inq-website">Website</label>
                  <input id="inq-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="inquiry-honeypot" />
                </div>
                <div>
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink/75">
                    <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-[#0a0a0a]" data-testid="inquiry-consent" {...aria("consent")} />
                    <span>
                      {t("inquiry.consentPre")}
                      <Link to={lp("privacy")} target="_blank" className="underline underline-offset-4 hover:text-ink">{t("inquiry.consentLink")}</Link>
                      {t("inquiry.consentPost")}
                    </span>
                  </label>
                  {errors.consent && <p id="inq-consent-error" className="mt-1.5 text-[13px] text-[#9f1d24]" data-testid="inq-consent-error">{errors.consent}</p>}
                </div>
                {serverError && <p className="border-l-2 border-[#9f1d24] pl-3 text-sm text-[#9f1d24]" role="alert" data-testid="inquiry-server-error">{serverError}</p>}
                <button type="submit" disabled={phase === "sending"} className="btn btn-dark mt-2 w-full" data-testid="inquiry-submit">
                  {phase === "sending" ? `${t("inquiry.sending")}…` : t("inquiry.submit")}
                </button>
              </form>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
